/**
 * Origin Cell Component for Seeds Table
 *
 * Thin wrapper around shared OriginCell component for Seeds table usage.
 * Displays origin with Chariot rollup pattern using ChipsList for visual consistency.
 *
 * Features (via shared component):
 * - Chariot native capabilities → "Chariot"
 * - Third-party integrations → Integration name (e.g., "GitHub", "AWS")
 * - Manual imports → "Import"
 * - Future-proof: Supports both single origin (string) and multiple origins (string[])
 * - ChipsList display with tooltip showing all origins
 *
 * @see components/cells/OriginCell - Shared implementation
 */

import React from 'react';

import { OriginCell as SharedOriginCell } from '@/components/table/columns/cells/OriginCell';
import { useCellFilters } from '@/sections/seeds/components/CellFiltersContext';
import { Seed } from '@/types';

interface SeedOriginCellProps {
  seed: Seed;
}

/**
 * Seeds table origin cell renderer.
 *
 * Delegates to shared OriginCell component for consistent display across
 * Assets, Seeds, and other entity tables. Reads the active origin filters from
 * the seed cell filter context so matched origins are ordered first (canonical
 * ordering handled by the shared cell).
 *
 * @param seed - Seed entity with origin field (string or string[] in future)
 * @returns JSX element with formatted origin chips or dash if no origin
 */
export const OriginCell: React.FC<SeedOriginCellProps> = ({ seed }) => {
  const filters = useCellFilters();
  const activeOriginFilters = filters?.advancedFilters?.origins;

  return (
    <SharedOriginCell
      origins={seed.origins ?? seed.origin}
      activeOriginFilters={activeOriginFilters}
      data-testid="seed-origin-chips"
    />
  );
};
